import { useState, useEffect } from 'react';
import { getRulesets, getActiveRuleset, createRuleset, activateRuleset, type Ruleset } from '../api/client';
import { format } from 'date-fns';

const DEFAULT_RULESET: Omit<Ruleset, 'id'> = {
  version: '',
  name: '',
  description: '',
  global: {
    enabled: true,
    default_verdict: 'ALLOW',
    emergency_mode: false,
    emergency_threshold_otp_5m: 1000,
  },
  routes: {
    SIGNUP: {
      enabled: true,
      thresholds: { allow_below: 30, challenge_below: 70, block_above: 70 },
      require_telemetry: false,
      step_up_action: 'TURNSTILE',
    },
    LOGIN: {
      enabled: true,
      thresholds: { allow_below: 30, challenge_below: 70, block_above: 70 },
      require_telemetry: false,
      step_up_action: 'TURNSTILE',
    },
    OTP_START: {
      enabled: true,
      thresholds: { allow_below: 25, challenge_below: 60, block_above: 60 },
      require_telemetry: false,
      step_up_action: 'TURNSTILE',
    },
    OTP_VERIFY: {
      enabled: true,
      thresholds: { allow_below: 35, challenge_below: 75, block_above: 75 },
      require_telemetry: false,
    },
  },
  scoring: {
    weights: {
      HIGH_VELOCITY_DEVICE: 20,
      HIGH_VELOCITY_IP: 15,
      HEADLESS_AUTOMATION: 40,
      WEBDRIVER_DETECTED: 35,
      PROXY_VPN: 15,
      OTP_PUMPING_PATTERN: 35,
    },
    telemetry_missing_multiplier: 1.2,
    reputation_weight: 0.3,
  },
  velocity: {
    device: { otp_starts_15m: 3, otp_starts_1h: 10, signups_24h: 5 },
    ip: { otp_starts_1m: 5, otp_starts_15m: 20, signups_1h: 10 },
    phone: { otp_starts_15m: 2, otp_starts_1h: 5 },
    email: { signups_24h: 3 },
  },
  lists: {
    use_allowlist: true,
    use_denylist: true,
  },
  features: {
    telemetry_required: false,
    reputation_enabled: true,
    adaptive_thresholds: false,
    impossible_travel_check: true,
  },
};

export default function RulesetsPage() {
  const [rulesets, setRulesets] = useState<Ruleset[]>([]);
  const [activeRuleset, setActiveRuleset] = useState<Ruleset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingRuleset, setEditingRuleset] = useState<Omit<Ruleset, 'id'>>(DEFAULT_RULESET);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setError(null);

    const [rulesetsResult, activeResult] = await Promise.all([
      getRulesets(),
      getActiveRuleset(),
    ]);

    if (rulesetsResult.error) {
      setError(rulesetsResult.error);
    } else if (rulesetsResult.data) {
      setRulesets(rulesetsResult.data.rulesets);
    }

    if (activeResult.data) {
      setActiveRuleset(activeResult.data.ruleset);
    }

    setLoading(false);
  }

  async function handleActivate(rulesetId: number) {
    const result = await activateRuleset(rulesetId);
    if (result.error) {
      setError(result.error);
    } else {
      loadData();
    }
  }

  async function handleSave() {
    if (!editingRuleset.version || !editingRuleset.name) {
      setError('Version and name are required');
      return;
    }

    setSaving(true);
    const result = await createRuleset(editingRuleset);

    if (result.error) {
      setError(result.error);
    } else {
      setShowEditor(false);
      setEditingRuleset(DEFAULT_RULESET);
      loadData();
    }
    setSaving(false);
  }

  function handleCloneRuleset(ruleset: Ruleset) {
    const cloned = { ...ruleset, version: `${ruleset.version}-copy`, name: `${ruleset.name} (Copy)` };
    // @ts-expect-error - removing id for clone
    delete cloned.id;
    setEditingRuleset(cloned);
    setShowEditor(true);
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Rulesets</h1>
        <button className="btn btn-primary" onClick={() => setShowEditor(true)}>
          Create Ruleset
        </button>
      </div>

      {error && (
        <div className="bg-red-500/20 text-red-400 p-4 rounded-lg mb-4">
          {error}
          <button onClick={() => setError(null)} className="ml-4 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Active ruleset summary */}
      {activeRuleset && (
        <div className="card mb-6 border-green-500/50">
          <div className="flex items-center gap-2 mb-4">
            <span className="badge badge-allow">ACTIVE</span>
            <h2 className="text-lg font-semibold">{activeRuleset.name}</h2>
            <span className="text-gray-400">v{activeRuleset.version}</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-400">Status</div>
              <div>{activeRuleset.global.enabled ? 'Enabled' : 'Disabled'}</div>
            </div>
            <div>
              <div className="text-gray-400">Emergency Mode</div>
              <div>{activeRuleset.global.emergency_mode ? 'ON' : 'OFF'}</div>
            </div>
            <div>
              <div className="text-gray-400">Reputation</div>
              <div>{activeRuleset.features.reputation_enabled ? 'Enabled' : 'Disabled'}</div>
            </div>
            <div>
              <div className="text-gray-400">Telemetry Required</div>
              <div>{activeRuleset.features.telemetry_required ? 'Yes' : 'No'}</div>
            </div>
          </div>
        </div>
      )}

      {/* Rulesets list */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Name</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rulesets.map((ruleset) => (
              <tr key={ruleset.id}>
                <td className="font-mono">{ruleset.version}</td>
                <td>{ruleset.name}</td>
                <td>
                  {ruleset.id === activeRuleset?.id ? (
                    <span className="badge badge-allow">Active</span>
                  ) : (
                    <span className="badge bg-gray-600">Inactive</span>
                  )}
                </td>
                <td>
                  {ruleset.created_at
                    ? format(new Date(ruleset.created_at), 'MMM d, yyyy HH:mm')
                    : '-'}
                </td>
                <td className="space-x-2">
                  {ruleset.id !== activeRuleset?.id && (
                    <button
                      onClick={() => handleActivate(ruleset.id)}
                      className="text-green-400 hover:text-green-300"
                    >
                      Activate
                    </button>
                  )}
                  <button
                    onClick={() => handleCloneRuleset(ruleset)}
                    className="text-blue-400 hover:text-blue-300"
                  >
                    Clone
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ruleset editor modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto m-4">
            <div className="p-6 border-b border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold">Create Ruleset</h2>
              <button
                onClick={() => setShowEditor(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Version *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="v1.0.0"
                    value={editingRuleset.version}
                    onChange={(e) =>
                      setEditingRuleset({ ...editingRuleset, version: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Name *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Production Ruleset"
                    value={editingRuleset.name}
                    onChange={(e) =>
                      setEditingRuleset({ ...editingRuleset, name: e.target.value })
                    }
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  className="input"
                  rows={2}
                  value={editingRuleset.description || ''}
                  onChange={(e) =>
                    setEditingRuleset({ ...editingRuleset, description: e.target.value })
                  }
                />
              </div>

              {/* Global settings */}
              <div className="card">
                <h3 className="font-semibold mb-4">Global Settings</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingRuleset.global.enabled}
                      onChange={(e) =>
                        setEditingRuleset({
                          ...editingRuleset,
                          global: { ...editingRuleset.global, enabled: e.target.checked },
                        })
                      }
                    />
                    <span>Enabled</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingRuleset.global.emergency_mode}
                      onChange={(e) =>
                        setEditingRuleset({
                          ...editingRuleset,
                          global: { ...editingRuleset.global, emergency_mode: e.target.checked },
                        })
                      }
                    />
                    <span>Emergency Mode</span>
                  </label>
                  <div>
                    <label className="block text-xs text-gray-400">OTP Threshold (5m)</label>
                    <input
                      type="number"
                      className="input"
                      value={editingRuleset.global.emergency_threshold_otp_5m}
                      onChange={(e) =>
                        setEditingRuleset({
                          ...editingRuleset,
                          global: {
                            ...editingRuleset.global,
                            emergency_threshold_otp_5m: parseInt(e.target.value),
                          },
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Feature flags */}
              <div className="card">
                <h3 className="font-semibold mb-4">Features</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingRuleset.features.reputation_enabled}
                      onChange={(e) =>
                        setEditingRuleset({
                          ...editingRuleset,
                          features: { ...editingRuleset.features, reputation_enabled: e.target.checked },
                        })
                      }
                    />
                    <span>Reputation</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingRuleset.features.telemetry_required}
                      onChange={(e) =>
                        setEditingRuleset({
                          ...editingRuleset,
                          features: { ...editingRuleset.features, telemetry_required: e.target.checked },
                        })
                      }
                    />
                    <span>Require Telemetry</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingRuleset.features.impossible_travel_check}
                      onChange={(e) =>
                        setEditingRuleset({
                          ...editingRuleset,
                          features: { ...editingRuleset.features, impossible_travel_check: e.target.checked },
                        })
                      }
                    />
                    <span>Impossible Travel</span>
                  </label>
                </div>
              </div>

              {/* JSON preview */}
              <div className="card">
                <h3 className="font-semibold mb-2">JSON Preview</h3>
                <pre className="bg-gray-900 p-4 rounded text-xs overflow-auto max-h-64">
                  {JSON.stringify(editingRuleset, null, 2)}
                </pre>
              </div>
            </div>

            <div className="p-6 border-t border-gray-700 flex justify-end gap-2">
              <button className="btn btn-secondary" onClick={() => setShowEditor(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Create Ruleset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
